import { useEffect, useMemo, useState } from 'react';
import { Building2, Check, Globe, Loader2, Search } from 'lucide-react';

import { Button } from './button';
import { Input } from './input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from './dropdown-menu';
import type { ScopeMode, ScopeOrganization, ScopeTenant } from '@workspace/scope-context';

export interface ScopeSelectorProps {
  mode: ScopeMode;
  allowGlobal: boolean;
  activeTenant?: ScopeTenant | null;
  tenants: ScopeTenant[];
  organizations?: ScopeOrganization[];
  loading?: boolean;
  variant?: 'admin' | 'tenant';
  onSearch?: (query: string) => void;
  onSelectTenant: (tenantId: string) => void;
  onSelectGlobal?: () => void;
}

export function ScopeSelector({
  mode,
  allowGlobal,
  activeTenant,
  tenants,
  organizations = [],
  loading = false,
  variant = 'admin',
  onSearch,
  onSelectTenant,
  onSelectGlobal
}: ScopeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => {
    if (variant !== 'admin' || !onSearch) {
      return;
    }
    const handle = window.setTimeout(() => onSearch(searchValue.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [variant, searchValue, onSearch]);

  const activeLabel = useMemo(() => {
    if (mode === 'global') {
      return 'Global scope';
    }
    if (activeTenant?.name) {
      return activeTenant.name;
    }
    return 'Select tenant';
  }, [mode, activeTenant]);

  const handleTenantSelect = (tenantId: string) => {
    setOpen(false);
    onSelectTenant(tenantId);
  };

  const showEmptyState = tenants.length === 0 && !loading;
  const organizationSections = useMemo(
    () =>
      organizations
        .filter(org => (org.tenants?.length ?? 0) > 0)
        .map(org => ({
          org,
          tenants: org.tenants ?? []
        })),
    [organizations]
  );
  const ungroupedTenants = useMemo(
    () => tenants.filter(tenant => !tenant.organizationId),
    [tenants]
  );

  const renderTenantRow = (tenant: ScopeTenant, showBorder = true) => {
    const isActive = mode === 'tenant' && tenant.id === activeTenant?.id;
    return (
      <button
        type="button"
        key={tenant.id}
        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/40 ${showBorder ? 'border-b border-border/40 last:border-b-0' : ''}`}
        onClick={() => handleTenantSelect(tenant.id)}
      >
        <div className="flex flex-col">
          <span className="font-medium text-foreground">{tenant.name ?? 'Unnamed tenant'}</span>
          {tenant.slug ? <span className="text-xs text-muted-foreground">{tenant.slug}</span> : null}
        </div>
        {isActive ? <Check className="h-4 w-4 text-primary" aria-hidden /> : null}
      </button>
    );
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="min-w-[180px] justify-between">
          <span className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden />
            <span className="truncate">{activeLabel}</span>
          </span>
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 space-y-3 p-3">
        {variant === 'admin' ? (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Search tenants</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchValue}
                onChange={event => setSearchValue(event.target.value)}
                placeholder="Start typing a tenant name…"
                className="pl-9"
              />
            </div>
          </div>
        ) : null}

        {variant === 'admin' && allowGlobal && onSelectGlobal ? (
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-left text-sm hover:border-primary hover:bg-muted/40"
            onClick={() => {
              setOpen(false);
              onSelectGlobal();
            }}
          >
            <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
            <div className="flex flex-1 flex-col">
              <span className="font-medium">Global scope</span>
              <span className="text-xs text-muted-foreground">View all tenants</span>
            </div>
            {mode === 'global' ? <Check className="h-4 w-4 text-primary" aria-hidden /> : null}
          </button>
        ) : null}

        <div className="max-h-72 space-y-3 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 rounded-md border border-border/60 px-3 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading tenants…
            </div>
          ) : null}
          {showEmptyState ? (
            <div className="rounded-md border border-border/60 px-3 py-6 text-sm text-muted-foreground">
              No tenants found.
            </div>
          ) : null}
          {!loading && !showEmptyState ? (
            <>
              {variant === 'admin' ? (
                <>
                  {organizationSections.map(({ org, tenants: orgTenants }, index) => {
                    const orgKey = org.id?.toString() ?? org.slug ?? `${org.name ?? 'organization'}-${index}`;
                    return (
                      <div key={orgKey} className="rounded-md border border-border/60">
                        <div className="border-b border-border/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {org.name ?? 'Unnamed organization'}
                        </div>
                        {orgTenants.map((tenant, index) =>
                          renderTenantRow(tenant, index !== orgTenants.length - 1)
                        )}
                      </div>
                    );
                  })}
                  {ungroupedTenants.length > 0 ? (
                    <div className="rounded-md border border-dashed border-border/60">
                      <div className="border-b border-border/60 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Ungrouped tenants
                      </div>
                      {ungroupedTenants.map((tenant, index) =>
                        renderTenantRow(tenant, index !== ungroupedTenants.length - 1)
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="rounded-md border border-border/60">
                  {tenants.map((tenant, index) => renderTenantRow(tenant, index !== tenants.length - 1))}
                </div>
              )}
            </>
          ) : null}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
